import { useEffect, useState } from 'react'
import { Box, Chip, Stack, Typography } from '@mui/material'

import { Alert, Button, Card, Input } from '@repo/ui'
import { useSnackbar } from 'contexts'
import { useCanAccess } from 'hooks'
import { useInsightsSpend, useSetInsightsCap } from 'hooks/api/useArticleCovers'

const usd = (n: number) => `$${n.toFixed(2)}`
const KIND: Record<string, string> = { concepts: 'Concepts', tests: 'Images and QA', upscale: 'Upscales' }

/**
 * What Insights costs, across every piece (Spencer, 2026-11-04: the cost section is "moot on a
 * specific article and more a top-level insights analytics"). Each row says what it measures, and a
 * cost nothing records says so rather than showing $0.
 */
export const InsightsSpendCard = () => {
  const { data, error } = useInsightsSpend()
  const { isHasAccess: isAdmin } = useCanAccess({ role: 'platform_admin' })
  const { open: notify } = useSnackbar()
  const setCap = useSetInsightsCap()
  const [cap, setCapInput] = useState('')
  useEffect(() => {
    if (data) setCapInput(String(data.cap.capUsd))
  }, [data?.cap.capUsd]) // eslint-disable-line react-hooks/exhaustive-deps
  if (error) return <Alert severity="error" title="Could not load the spend" description={(error as Error).message} />
  if (!data) return null
  const pct = data.cap.capUsd > 0 ? Math.min(100, (data.cap.spentUsd / data.cap.capUsd) * 100) : 100
  return (
    <Card title="Spend" sx={{ p: 4, mb: 4 }}>
      <Stack spacing={3}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
          <Box>
            <Typography variant="body4" color="text.secondary">Cover generation, all time</Typography>
            <Typography variant="h5">{usd(data.covers.totalUsd)}</Typography>
            <Typography variant="body4" color="text.secondary">
              {data.covers.byKind.map((k) => `${KIND[k.kind] ?? k.kind} ${usd(k.usd)}`).join(' · ') || 'No paid runs yet'}. Measured per run, from this tool&apos;s database.
            </Typography>
          </Box>
          <Box>
            <Typography variant="body4" color="text.secondary">ElevenLabs narration</Typography>
            <Typography variant="h5">{data.narration ? `~${usd(data.narration.usd)}` : 'Not available'}</Typography>
            <Typography variant="body4" color="text.secondary">
              {data.narration
                ? `${data.narration.pieces} pieces, ${data.narration.chars.toLocaleString('en-US')} characters, ${Math.round(data.narration.minutes)} minutes at the listed rate. Audio on the site only: re-recorded takes are not counted, so the real spend is higher.`
                : 'The narration manifest could not be read.'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body4" color="text.secondary">Article generation</Typography>
            <Typography variant="h5">Not recorded</Typography>
            <Typography variant="body4" color="text.secondary">Drafts are written outside this tool, and nothing logs what they cost.</Typography>
          </Box>
        </Box>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems={{ md: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <Typography variant="body3" sx={{ fontWeight: 600 }}>
                Covers: {usd(data.cap.spentUsd)} of {usd(data.cap.capUsd)} in {data.cap.month}
              </Typography>
              <Chip size="small" label={data.cap.mode === 'live' ? 'Live' : 'Mock'} color={data.cap.mode === 'live' ? 'success' : 'default'} />
            </Stack>
            <Box sx={(t) => ({ mt: 1, height: 6, borderRadius: 3, backgroundColor: t.palette.primary[100], overflow: 'hidden' })}>
              <Box sx={{ width: `${pct}%`, height: '100%', backgroundColor: pct >= 100 ? 'error.main' : 'primary.main' }} />
            </Box>
            <Typography variant="body4" color="text.secondary">
              A cover run starts only if this month&apos;s spend plus the run&apos;s highest estimate stays within the cap.
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="flex-end">
            <Box sx={{ width: 140 }}>
              <Input label="Monthly cap (USD)" value={cap} onChange={setCapInput} disabled={!isAdmin} />
            </Box>
            <Button
              variant="secondary"
              size="small"
              disabled={!isAdmin || setCap.isPending || Number(cap) === data.cap.capUsd || !Number.isFinite(Number(cap))}
              onClick={() =>
                setCap.mutate(Number(cap), {
                  onSuccess: (c) => notify({ type: 'success', text: `Cap set to $${c.capUsd.toFixed(2)} a month` }),
                  onError: (e) => notify({ type: 'error', text: (e as Error).message }),
                })
              }
            >
              Save cap
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  )
}
